import { useState, type ReactNode } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { HttpStatusCode, type AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { Form } from '@components/form';
import {
  nonQualityInterventionStatuses,
  qualitySurveyFieldId,
} from '@constants/survey.constant';
import { ESurveyGroup, ESurveyType } from '@enums/survey.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { qualitySurveyTokenDecoder } from '@helpers/qualitySurveyToken.helper';
import { useIsMobile } from '@hooks/useIsMobile.hook';
import { cn } from '@lib/utils';
import {
  notCompletedSurveySchema,
  type NotCompletedSurveyDto,
  type SubmitSurveyPayload,
} from '@services/surveys/dtos/submit.dto';
import { surveyService } from '@services/surveys/service';
import type { SubmitSurveyOutput } from '@services/surveys/types/submit.type';

import { OpenFeedback } from '../../@components/fields/OpenFeedback';
import { GroupHeader } from '../../@components/GroupHeader';
import { NavigationButtons } from '../../@components/NavigationButtons';
import { HasScheduledEndDate } from './@components/fields/HasScheduledEndDate';
import { ScheduledEndDate } from './@components/fields/ScheduledEndDate';

const surveySearchParamsSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute(
  '/_layout/$lang/quality-survey/_layout/not-completed/',
)({
  component: RouteComponent,
  validateSearch: search => surveySearchParamsSchema.parse(search),
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => {
    const decodedToken = qualitySurveyTokenDecoder(deps.token!);

    return {
      data: decodedToken,
      isNonQuality: nonQualityInterventionStatuses.has(
        decodedToken.parentStatus,
      ),
    };
  },
});

function RouteComponent() {
  const { data } = Route.useLoaderData();
  const { lang } = Route.useParams();
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();

  const [currentStep, setCurrentStep] = useState<number>(0);

  const form = useForm<NotCompletedSurveyDto>({
    defaultValues: {
      [qualitySurveyFieldId.hasScheduledEndDate]: 'false',
      [qualitySurveyFieldId.openFeedback]: '',
    },
    mode: 'onChange',
    resolver: zodResolver(notCompletedSurveySchema),
    shouldUnregister: true,
  });

  const { isPending: isSubmittingSurvey, mutate: submitSurvey } = useMutation<
    SubmitSurveyOutput,
    AxiosError,
    SubmitSurveyPayload
  >({
    mutationFn: surveyService.submit,
    onError: error => {
      if (error.status === HttpStatusCode.Gone) {
        throw redirect({
          to: '/$lang/quality-survey/expired',
          params: {
            lang,
          },
        });
      }

      handleRequestError(error);
    },
    onSuccess: () => {
      navigate({
        params: { lang },
        to: '/$lang/quality-survey/not-completed/submitted',
        replace: true,
      });
    },
  });

  const handleSubmit = form.handleSubmit(
    values => {
      submitSurvey({
        body: {
          scheduledEndDate: values.scheduledEndDate,
          hasScheduledEndDate: values.hasScheduledEndDate === 'true',
        },
        id: data.id,
      });
    },
    error => console.log('error', error),
  );

  const visibleGroups = buildNotCompletedSurveyGroups(data.type);

  const isLastStep = currentStep === visibleGroups.length - 1;

  async function goNext() {
    const currentGroup = visibleGroups[currentStep];
    const isValid = await form.trigger(currentGroup.fieldIds);

    if (!isValid) {
      return;
    }

    setCurrentStep(prev => prev + 1);
  }

  function goBack() {
    setCurrentStep(prev => prev - 1);
  }

  const groupContent: Partial<Record<ESurveyGroup, ReactNode>> = {
    [ESurveyGroup.Feedback]: <OpenFeedback />,
    [ESurveyGroup.Rescheduling]: (
      <>
        <HasScheduledEndDate />
        <ScheduledEndDate />
      </>
    ),
  };

  return (
    <Form.Provider {...form}>
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
        <div
          className={cn('flex flex-col gap-8 py-6', {
            'pb-4': isMobile,
          })}
        >
          {data.providerName && (
            <h3 className="text-lg font-semibold">{data.providerName}</h3>
          )}

          <div>
            {visibleGroups.map((group, index) => (
              <div
                className={cn('w-full', { hidden: index !== currentStep })}
                key={group.id}
              >
                <section className="flex flex-col">
                  <GroupHeader titleKey={group.titleKey} />
                  {groupContent[group.id]}
                </section>
              </div>
            ))}
          </div>
        </div>
        <NavigationButtons
          canGoBack={currentStep > 0}
          isLastStep={isLastStep}
          isLoading={isSubmittingSurvey}
          onBack={goBack}
          onNext={goNext}
          onSubmit={handleSubmit}
        />
      </div>
    </Form.Provider>
  );
}

type SurveyGroup = {
  fieldIds: Array<keyof NotCompletedSurveyDto>;
  id: ESurveyGroup;
  titleKey: string;
};

function buildNotCompletedSurveyGroups(type: ESurveyType): Array<SurveyGroup> {
  return [
    ...(type === ESurveyType.Intervention
      ? [
          {
            fieldIds: [
              qualitySurveyFieldId.hasScheduledEndDate,
              qualitySurveyFieldId.scheduledEndDate,
            ],
            id: ESurveyGroup.Rescheduling,
            titleKey: 'survey.group.rescheduling',
          },
        ]
      : []),
    {
      fieldIds: [qualitySurveyFieldId.openFeedback],
      id: ESurveyGroup.Feedback,
      titleKey: 'survey.group.feedback',
    },
  ];
}
